<template>
    <div class="notification-push-v3-container">
        <v-app class="ppv2-bg-white dark:ppv2-bg-dark ppv2-rounded-3xl ppv2-p-6">
            <!-- Header -->
            <div class="ppv2-flex ppv2-items-center">
                <Header :title="id ? $t('notifications_push.edit') : $t('notifications_push.add')" />
                <span v-if="id" class="ppv2-text-sm ppv2-text-gray-500 ppv2-ml-2">#{{ id }}</span>
            </div>

            <div class="ppv2-w-full">
                <div class="ppv2-flex ppv2-flex-col md:ppv2-flex-row" :style="{ gap: '2%' }">
                    <!-- Left Column: Main Form -->
                    <div class="ppv2-w-full md:ppv2-w-[63%]">
                        <v-card
                            class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-mb-6 ppv2-shadow-none"
                        >
                            <div class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-p-6">
                                <h2 class="poppinsSemiBold ppv2-text-black mb-0" :style="{ fontSize: normalizeFontSize(18) }">
                                    {{ $t('notifications_push.general_info') }}
                                </h2>
                            </div>
                            <v-divider class="ppv2-m-0"></v-divider>
                            <div class="ppv2-p-6 ppv2-space-y-6">
                                <!-- Nom interne -->
                                <div>
                                    <CustomInput
                                        :label="$t('notifications_push.system_title_label')"
                                        v-model="formData.system_title"
                                        :required="true"
                                        :placeholder="$t('notifications_push.system_title_placeholder')"
                                        :errorText="formErrors.system_title"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                    />
                                </div>

                                <!-- Titre de la notification -->
                                <div>
                                    <CustomInput
                                        id="v3-title-notif"
                                        v-model="formData.title_notif"
                                        :label="$t('notifications_push.title_notif_label')"
                                        :placeholder="$t('notifications_push.title_notif_placeholder')"
                                        :required="true"
                                        :maxlength="40"
                                        :errorText="formErrors.title_notif"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                        @input="clearError('title_notif')"
                                    />
                                </div>

                                <!-- Message -->
                                <div>
                                    <BaseTextarea
                                        id="v3-desc-notif"
                                        v-model="formData.desc_notif"
                                        :label="$t('notifications_push.desc_notif_label')"
                                        :placeholder="$t('notifications_push.description_placeholder')"
                                        :useEditor="true"
                                        :required="true"
                                        :maxlength="120"
                                        @input="clearError('desc_notif')"
                                    />
                                    <p v-if="formErrors.desc_notif" class="ppv2-text-[#ed4b9e] ppv2-text-sm">
                                        {{ formErrors.desc_notif }}
                                    </p>
                                </div>

                                <!-- Configuration Temporelle -->
                                <div class="ppv2-grid ppv2-grid-cols-1 md:ppv2-grid-cols-2 ppv2-gap-6">
                                    <div>
                                        <BaseTimeInput
                                            v-model="formData.send_time"
                                            :label="$t('notifications_push.columns.send_time')"
                                            placeholder="09:00"
                                            :required="true"
                                            :step="15"
                                            input-class="rounded-md px-3 w-full"
                                            :customSizeLabel="15"
                                            @input="clearError('send_time')"
                                        />
                                        <p v-if="formErrors.send_time" class="ppv2-text-[#ed4b9e] ppv2-text-sm">
                                            {{ formErrors.send_time }}
                                        </p>
                                    </div>

                                    <div>
                                        <BaseDateInput
                                            :label="$t('notifications_push.columns.end_date')"
                                            v-model="formData.end_date"
                                            :required="true"
                                            :minDate="formData.id ? null : minDate"
                                            :customSizeLabel="15"
                                            inputClass="!ppv2-rounded-lg !ppv2-h-[35px] !ppv2-min-h-[35px]"
                                            customLabelClass="poppinsLight ppv2-text-black"
                                        />
                                        <p v-if="formErrors.end_date" class="ppv2-text-[#ed4b9e] ppv2-text-sm">
                                            {{ formErrors.end_date }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </v-card>
                    </div>

                    <!-- Right Column: Status & Summary -->
                    <div class="ppv2-w-full md:ppv2-w-[35%]">
                        <div
                            class="ppv2-text-black ppv2-rounded-[10px] ppv2-border ppv2-border-black ppv2-border-opacity-20 ppv2-p-6"
                        >
                            <!-- Summary Details -->
                            <div class="ppv2-space-y-4">
                                <div
                                    class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-p-3 ppv2-border ppv2-border-black ppv2-border-opacity-20 ppv2-rounded-lg bg-purple ppv2-bg-opacity-[4%]"
                                >
                                    <div class="ppv2-flex ppv2-items-center ppv2-gap-3">
                                        <CalendarDays :size="24" class="text-purple" :stroke-width="1" />
                                        <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                                            {{ $t('notifications_push.columns.end_date') }}
                                        </span>
                                    </div>
                                    <span class="poppinsMedium" :style="{ fontSize: normalizeFontSize(15) }">
                                        {{ formData.end_date || '-' }}
                                    </span>
                                </div>
                                <div
                                    class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-p-3 ppv2-border ppv2-border-black ppv2-border-opacity-20 ppv2-rounded-lg bg-purple ppv2-bg-opacity-[4%]"
                                >
                                    <div class="ppv2-flex ppv2-items-center ppv2-gap-3">
                                        <CalendarDays :size="24" class="text-purple" :stroke-width="1" />
                                        <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                                            {{ $t('notifications_push.columns.send_time') }}
                                        </span>
                                    </div>
                                    <span class="poppinsMedium" :style="{ fontSize: normalizeFontSize(15) }">
                                        {{ formData.send_time || '-' }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Bottom Actions -->
                <div class="ppv2-flex ppv2-flex-col md:ppv2-flex-row" :style="{ gap: '2%' }">
                    <div class="ppv2-w-full md:ppv2-w-[63%]">
                        <div
                            class="ppv2-flex ppv2-justify-between ppv2-items-center"
                            :style="{ marginTop: normalizeSize(24), marginBottom: normalizeSize(40, true) }"
                        >
                            <BackButton type="button" :baseUrl="urlIndex" />
                            <BaseButton
                                v-if="canEditOrDelete(formData)"
                                variant="primary"
                                :loading="loading"
                                @click="submitForm"
                                :customHeight="48"
                            >
                                <v-icon left color="white">mdi-check</v-icon>
                                {{ $t('common.validate') }}
                            </BaseButton>
                        </div>
                    </div>
                </div>
            </div>
        </v-app>
    </div>
</template>

<script>
import normalizeSize from '../../../../utils/normalizeSize'
import normalizeFontSize from '../../../../utils/normalizeFontSize'
import Header from '../../../Shared/Header.vue'
import BackButton from '../../../Shared/BackButton.vue'
import CustomInput from '../../../Inputs/CustomInput.vue'
import BaseTextarea from '../../../Shared/BaseTextarea.vue'
import BaseTimeInput from '../../../Shared/BaseTimeInput.vue'
import BaseDateInput from '../../../Shared/BaseDateInput.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import { CalendarDays } from 'lucide-vue'
import ApiManager from './ApiManager.js'
export default {
    name: 'NotificationPushV3Form',

    components: {
        Header,
        BackButton,
        CustomInput,
        BaseTextarea,
        BaseTimeInput,
        BaseDateInput,
        BaseButton,
        CalendarDays,
    },

    props: {
        urlIndex: {
            type: String,
            default: '/back-office/v3/communications/notifications',
        },
        id: {
            type: Number,
            default: null,
        },
    },

    data() {
        return {
            minDate: this.toTomorrow(),
            formData: {
                id: this.id,
                system_title: '',
                title_notif: '',
                desc_notif: '',
                send_time: '',
                end_date: '',
                send: false,
            },
            formErrors: {},
            loading: false,
        }
    },

    created() {
        if (this.id) {
            this.loadNotification()
        }
    },

    methods: {
        normalizeSize,
        normalizeFontSize,
        canEditOrDelete(item) {
            const isSent = Boolean(Number(item.send))
            return !isSent
        },
        async loadNotification() {
            try {
                const notif = await ApiManager.get(this.id)
                if (notif) {
                    const fields = ['system_title', 'title_notif', 'desc_notif', 'send_time', 'end_date', 'send']
                    fields.forEach((field) => {
                        if (Object.prototype.hasOwnProperty.call(notif, field)) {
                            this.formData[field] = notif[field]
                        }
                    })
                }
            } catch (e) {
                console.error('Erreur chargement notification:', e)
            }
        },

        resetForm() {
            this.formData = {
                id: this.id,
                system_title: '',
                title_notif: '',
                desc_notif: '',
                send_time: '',
                end_date: '',
                send: false,
            }
            this.formErrors = {}
        },

        async submitForm() {
            if (!this.validateForm()) return

            this.loading = true
            try {
                const payload = { ...this.formData }
                if (!payload.id) {
                    delete payload.id
                }
                const data = await ApiManager.update(JSON.stringify(payload))
                this.$toast.success(data.message || this.$t('notifications_push.messages.save_success'))
                if (!this.formData.id) {
                    this.resetForm()
                }
            } catch (e) {
                this.$toast.error(this.$t('notifications_push.messages.submit_error'))
            } finally {
                this.loading = false
            }
        },

        validateForm() {
            this.formErrors = {}

            if (!this.formData.system_title) {
                this.formErrors.system_title = this.$t('notifications_push.messages.validation.system_title_required')
            }
            if (!this.formData.title_notif) {
                this.formErrors.title_notif = this.$t('notifications_push.messages.validation.title_required')
            }
            if (!this.formData.desc_notif) {
                this.formErrors.desc_notif = this.$t('notifications_push.messages.validation.description_required')
            }
            if (!this.formData.send_time) {
                this.formErrors.send_time = this.$t('notifications_push.messages.validation.time_required')
            }
            if (!this.formData.end_date) {
                this.formErrors.end_date = this.$t('notifications_push.messages.validation.date_required')
            }

            return Object.keys(this.formErrors).length === 0
        },

        clearError(field) {
            if (this.formErrors[field]) {
                this.$delete(this.formErrors, field)
            }
        },

        toTomorrow() {
            const today = new Date()
            today.setDate(today.getDate() + 1)
            return today.toISOString().split('T')[0]
        },
    },
}
</script>

<style scoped lang="scss">
.notification-push-v3-container {
    padding: 0;
}
</style>

<style lang="scss">
#main-container:has(.notification-push-v3-container) {
    background: transparent !important;
}
</style>
